import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import FundingAIAdvisorChat from '../../components/funding/FundingAIAdvisorChat';
import FundingAIAdvisorClientSelector from '../../components/funding/FundingAIAdvisorClientSelector';
import FundingAIAdvisorContextPanel from '../../components/funding/FundingAIAdvisorContextPanel';
import FundingAIAdvisorPortfolioPanel from '../../components/funding/FundingAIAdvisorPortfolioPanel';
import FundingAIRecommendationList from '../../components/funding/FundingAIRecommendationList';
import FundingPanel from '../../components/funding/FundingPanel';
import FundingSectionPage from '../../components/funding/FundingSectionPage';
import { useFundingWorkspace } from '../../context/FundingWorkspaceContext';
import { useFundingAIAdvisor } from '../../hooks/funding/useFundingAIAdvisor';
import { fetchFundingClients } from '../../services/funding/fundingClientsService';

const FundingAIAdvisorPage = () => {
  const { workspaceAgencyId, isPlatformContext, requiresAgencySelection } = useFundingWorkspace();
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(searchParams.get('client_id') || '');
  const [savingId, setSavingId] = useState('');
  const initialPrompt = searchParams.get('prompt') || '';
  const [promptHandled, setPromptHandled] = useState(false);

  const agencyId = isPlatformContext ? workspaceAgencyId || undefined : workspaceAgencyId;
  const selectedClient = useMemo(
    () => clients.find((client) => client.id === selectedClientId) || null,
    [clients, selectedClientId],
  );

  const {
    context,
    messages,
    recommendations,
    loading,
    sending,
    generating,
    error,
    sendMessage,
    generateRecommendations,
    updateRecommendationStatus,
    refresh,
  } = useFundingAIAdvisor({
    agencyId,
    clientId: selectedClientId || undefined,
    autoLoad: Boolean(agencyId),
  });

  const loadClients = useCallback(async () => {
    if (!agencyId) {
      setClients([]);
      return;
    }
    setClientsLoading(true);
    try {
      const result = await fetchFundingClients({ agencyId });
      setClients(result.clients || []);
    } catch {
      setClients([]);
    } finally {
      setClientsLoading(false);
    }
  }, [agencyId]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    if (!agencyId || !initialPrompt || promptHandled || loading) return;
    setPromptHandled(true);
    sendMessage(initialPrompt).catch(() => {});
  }, [agencyId, initialPrompt, promptHandled, loading, sendMessage]);

  const handleRecommendationAction = async (recommendation, status) => {
    if (!agencyId) return;
    setSavingId(recommendation.id);
    try {
      await updateRecommendationStatus(recommendation.id, status);
    } finally {
      setSavingId('');
    }
  };

  return (
    <FundingSectionPage
      title="AI Advisor"
      subtitle="Recommended funding strategy based on readiness, documents, matches, pipeline, and commission data."
      activeStage="readiness"
      actions={(
        <Button variant="outline" className="rounded-xl" onClick={refresh} disabled={loading}>
          Refresh context
        </Button>
      )}
    >
      {requiresAgencySelection ? (
        <div className="mb-4 rounded-xl border border-dashed border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-3 text-sm text-[var(--fg-muted)]">
          Select an agency in the header to use AI Advisor.
        </div>
      ) : null}

      {error ? (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200">
          {error}
        </div>
      ) : null}

      <div className="mb-4 grid gap-4 lg:grid-cols-[1fr_360px]">
        <FundingPanel title="Scope" subtitle="Client-specific or portfolio advisor mode">
          <FundingAIAdvisorClientSelector
            clients={clients}
            value={selectedClientId}
            onChange={setSelectedClientId}
            loading={clientsLoading}
          />
        </FundingPanel>
        <FundingPanel title="Recommendations" subtitle="Generate structured advisor actions">
          <Button
            className="rounded-xl"
            onClick={generateRecommendations}
            disabled={!agencyId || generating}
          >
            {generating ? 'Generating...' : 'Generate recommendations'}
          </Button>
        </FundingPanel>
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.4fr_1fr]">
        <FundingAIAdvisorChat
          messages={messages}
          sending={sending}
          hasClient={Boolean(selectedClientId)}
          onSend={sendMessage}
          onGenerate={generateRecommendations}
        />

        <div className="space-y-4">
          <FundingPanel title="Context" subtitle="Based on available FundGenius HQ data">
            <FundingAIAdvisorContextPanel
              context={context}
              loading={loading}
              client={selectedClient}
              agencyLabel={isPlatformContext ? agencyId : undefined}
            />
          </FundingPanel>

          {!selectedClientId ? (
            <FundingPanel title="Portfolio signals" subtitle="Agency-wide recommended focus areas">
              <FundingAIAdvisorPortfolioPanel context={context} />
            </FundingPanel>
          ) : null}

          <FundingPanel title="Open recommendations" subtitle="Accept, dismiss, or mark complete">
            <FundingAIRecommendationList
              recommendations={recommendations}
              loading={loading}
              savingId={savingId}
              onAccept={(rec) => handleRecommendationAction(rec, 'Accepted')}
              onDismiss={(rec) => handleRecommendationAction(rec, 'Dismissed')}
              onComplete={(rec) => handleRecommendationAction(rec, 'Completed')}
            />
          </FundingPanel>
        </div>
      </div>
    </FundingSectionPage>
  );
};

export default FundingAIAdvisorPage;
